import { useState } from 'react';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { NumberInput } from '../ui/number-input';
import { SupplierProduct } from '../../lib/types';
import { Settings, AlertTriangle, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

interface ThresholdUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: SupplierProduct;
  onSuccess: (productId: string, lowStockThreshold: number, reorderPoint: number) => Promise<void>;
}

export default function ThresholdUpdateModal({
  isOpen,
  onClose,
  product,
  onSuccess
}: ThresholdUpdateModalProps) {
  const [lowStockThreshold, setLowStockThreshold] = useState(product?.inventory?.lowStockThreshold || 10);
  const [reorderPoint, setReorderPoint] = useState(product?.inventory?.reorderPoint || 5);
  const [loading, setLoading] = useState(false);

  // Early return if product is not loaded
  if (!product) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await onSuccess(product._id, lowStockThreshold, reorderPoint);
      onClose();
    } catch (error) {
      console.error('Failed to update thresholds:', error);
    } finally {
      setLoading(false);
    }
  };

  const isInvalid = reorderPoint >= lowStockThreshold;

  return (
    <EnhancedDialog open={isOpen} onOpenChange={onClose}>
      <EnhancedDialogContent size="lg">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Update Inventory Thresholds
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Adjust low stock and reorder thresholds for {product?.product?.name || 'Product'} from {product?.supplier?.businessName || 'Supplier'}
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <EnhancedDialogBody>
          <form id="threshold-update-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Current Stock Overview */}
            <div className="grid grid-cols-3 gap-4 bg-muted/50 p-4 rounded-lg">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Current Stock</p>
                <p className="text-xl font-bold">{product?.inventory?.availableStock ?? 0}</p>
              </div>
              <div className="text-center border-l border-r border-muted-foreground/20">
                <p className="text-sm text-muted-foreground">Current Low</p>
                <p className="text-xl font-bold text-orange-600">{product?.inventory?.lowStockThreshold ?? 10}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Current Reorder</p>
                <p className="text-xl font-bold text-blue-600">{product?.inventory?.reorderPoint ?? 5}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Low Stock Threshold</Label>
                <div className="flex items-center gap-2">
                  <NumberInput
                    value={lowStockThreshold}
                    onChange={setLowStockThreshold}
                    min={1}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground w-12">units</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  When stock falls below this number, the product will be marked as "Low Stock".
                </p>
              </div>

              <div className="space-y-2">
                <Label>Reorder Point</Label>
                <div className="flex items-center gap-2">
                  <NumberInput
                    value={reorderPoint}
                    onChange={setReorderPoint}
                    min={1}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground w-12">units</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  The stock level at which a new order should be placed.
                </p>
              </div>
            </div>

            {isInvalid && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Invalid Configuration</AlertTitle>
                <AlertDescription>
                  Reorder point ({reorderPoint}) should be less than low stock threshold ({lowStockThreshold}).
                </AlertDescription>
              </Alert>
            )}

            <Alert className="bg-blue-50 text-blue-900 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertTitle>Recommendation</AlertTitle>
              <AlertDescription>
                We recommend setting the reorder point to at least 50% of the low stock threshold to ensure timely replenishment.
              </AlertDescription>
            </Alert>
          </form>
        </EnhancedDialogBody>

        <EnhancedDialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="threshold-update-form" disabled={loading || isInvalid}>
            {loading ? 'Updating...' : 'Save Changes'}
          </Button>
        </EnhancedDialogFooter>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}